import * as stylex from '@stylexjs/stylex';

/**
 * 1. KEYFRAMES
 */
const rotate = stylex.keyframes({
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
});



const float1 = stylex.keyframes({
    '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
    '50%': { transform: 'translateY(-20px) rotate(5deg)' },
});

const float2 = stylex.keyframes({
    '0%, 100%': { transform: 'translateY(0px) rotate(25deg)' },
    '50%': { transform: 'translateY(-15px) rotate(30deg)' },
});

const float3 = stylex.keyframes({
    '0%, 100%': { transform: 'translateY(0px) rotate(-8deg)' },
    '50%': { transform: 'translateY(-25px) rotate(-3deg)' },
});

const float4 = stylex.keyframes({
    '0%, 100%': { transform: 'translateY(0px) rotate(-12deg)' },
    '50%': { transform: 'translateY(-18px) rotate(-17deg)' },
});

/**
 * 2. MEDIA QUERIES
 */
const MEDIA_1024 = '@media (max-width: 1024px)';
const MEDIA_768 = '@media (max-width: 768px)';

const MEDIA_480 = '@media (max-width: 480px)';
const MEDIA_360 = '@media (max-width: 360px)';

/**
 * 3. STYLES
 */
export const styles = stylex.create({
    /* Page Layout */
    modelPage: {
        maxWidth: '1200px',
        marginInline: 'auto',
        paddingTop: 'var(--spacing-lg)',
        paddingBottom: 'var(--spacing-lg)',
        paddingInline: {
            default: 'var(--spacing-lg)',
            [MEDIA_1024]: 'var(--spacing-md)',
            [MEDIA_360]: 'var(--spacing-sm)',
        },
        color: 'var(--text-primary)',
    },
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: { default: 'var(--text-secondary)', ':hover': 'var(--primary)' },
        textDecoration: 'none',
        fontSize: 'var(--font-size-sm)',
        marginBottom: 'var(--spacing-md)',
        transitionProperty: 'color',
        transitionDuration: 'var(--transition-fast)',
    },
    pageTitle: {
        fontSize: { default: '2.5rem', [MEDIA_768]: '2rem', [MEDIA_480]: '1.75rem' },
        fontWeight: 700,
        margin: 0,
        color: 'var(--text-primary)',
    },

    /* Hero Section */
    heroSection: {
        position: 'relative',
        minHeight: { default: '600px', [MEDIA_1024]: '650px', [MEDIA_360]: '400px' },
        borderRadius: 'var(--radius-xl)',
        marginBottom: 'var(--spacing-3xl)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
    },
    speechHeroAnimated: {
        backgroundImage: `linear-gradient(135deg, var(--bg-card) 0%, rgba(59, 130, 246, 0.02) 25%, rgba(16, 185, 129, 0.02) 75%, var(--bg-card) 100%)`,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        '::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(16, 185, 129, 0.03) 0%, transparent 50%)`,
        },
        '::after': {
            content: '""',
            position: 'absolute',
            inset: '-50%',
            backgroundImage: `conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(59, 130, 246, 0.015) 45deg, transparent 90deg)`,
            animationName: rotate,
            animationDuration: '120s',
            animationTimingFunction: 'linear',
            animationIterationCount: 'infinite',
            opacity: 0.3,
        }
    },
    heroContent: {
        position: 'relative',
        zIndex: 2,
        paddingTop: { default: 'var(--spacing-3xl)', [MEDIA_768]: 'var(--spacing-lg)' },
        paddingBottom: { default: 'var(--spacing-3xl)', [MEDIA_768]: 'var(--spacing-lg)' },
        paddingInline: { default: 'var(--spacing-3xl)', [MEDIA_768]: 'var(--spacing-lg)', [MEDIA_360]: 'var(--spacing-md)' },
        maxWidth: { default: '600px', [MEDIA_768]: '100%' },
    },
    heroTitle: {
        fontSize: { default: '3rem', [MEDIA_768]: '2.2rem', [MEDIA_360]: '1.8rem' },
        fontWeight: 800,
        marginBlock: '0 var(--spacing-md)',
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    heroDescription: {
        fontSize: 'var(--font-size-lg)',
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        marginBottom: 'var(--spacing-xl)',
    },
    heroStats: {
        display: 'flex',
        gap: 'var(--spacing-lg)',
        flexWrap: 'wrap',
        flexDirection: { [MEDIA_768]: 'column' },
    },
    stat: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: 'var(--text-secondary)',
        fontWeight: 500,
    },
    statSvg: {
        color: 'var(--accent)',
    },
    creditCostBadge: {
        backgroundColor: 'var(--accent)',
        color: 'var(--text-on-accent)',
        paddingBlock: 'var(--spacing-sm)',
        paddingInline: 'var(--spacing-md)',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        marginTop: 'var(--spacing-md)',
        display: 'inline-block',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },

    /* Audio Elements */
    heroVoicePlayer: {
        marginTop: 'var(--spacing-md)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-md)',
    },
    heroAudioPlayer: {
        width: '100%',
        outline: 'none',
        borderRadius: 'var(--radius-md)',
        '::-webkit-media-controls-panel': {
            backgroundColor: '#9ca5afff',
            color: '#fff',
        }
    },
    audioLabel: { fontSize: 'var(--font-size-sm)', fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' },

    /* Floating Icons */
    floatingIconsContainer: { position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 },
    floatingIcon: { position: 'absolute', color: 'rgba(255, 255, 255, 0.15)', opacity: 0.8, transitionProperty: 'all', transitionDuration: '0.3s' },
    floatingIconSvg: { filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1))' },
    floatingIcon1: { top: '15%', right: '20%', animationName: float1, animationDuration: '8s', animationIterationCount: 'infinite', color: 'rgba(59, 130, 246, 0.2)' },
    floatingIcon2: { top: '35%', right: '10%', animationName: float2, animationDuration: '10s', animationIterationCount: 'infinite', color: 'rgba(16, 185, 129, 0.2)' },
    floatingIcon3: { top: '45%', right: '25%', animationName: float3, animationDuration: '12s', animationIterationCount: 'infinite', color: 'rgba(139, 92, 246, 0.2)' },
    floatingIcon4: { top: '60%', right: '15%', animationName: float4, animationDuration: '9s', animationIterationCount: 'infinite', color: 'rgba(245, 101, 101, 0.2)' },

    /* Important Note Section (FIXES THE TYPESCRIPT ERRORS) */
    importantNote: {
        backgroundImage: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1))',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'rgba(59, 130, 246, 0.2)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--spacing-xl)',
        marginBottom: 'var(--spacing-xl)',
        display: 'flex',
        gap: 'var(--spacing-lg)',
        alignItems: 'flex-start',
        flexDirection: { [MEDIA_768]: 'column' },
    },
    noteIcon: { flexShrink: 0 },
    noteContent: { flex: 1 },
    noteContentH4: {
        fontSize: '1.25rem',
        fontWeight: 600,
        marginBlock: '0 var(--spacing-sm)',
        color: 'var(--text-primary)'
    },
    noteContentP: {
        fontSize: 'var(--font-size-base)',
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        margin: 0
    },

    inlineChatButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '48px',
        height: '48px',
        backgroundColor: 'var(--bg-secondary)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: { default: 'var(--border)', ':hover': 'var(--border-hover)' },
        borderRadius: 'var(--radius-lg)',
        color: { default: 'var(--accent-light)', ':hover': 'var(--aimode)' },
        cursor: 'pointer',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
    },

    /* Gallery Base */
    gallerySection: { marginBottom: 'var(--spacing-3xl)' },
    sectionTitle: {
        fontSize: '2rem',
        fontWeight: 700,
        marginBlock: '0 var(--spacing-xl)',
        position: 'relative',
        '::after': {
            content: '""',
            position: 'absolute',
            bottom: '-8px',
            left: 0,
            width: '60px',
            height: '3px',
            backgroundImage: 'linear-gradient(90deg, var(--primary), var(--accent))',
            borderRadius: 'var(--radius-full)',
        }
    },
    galleryDescription: {
        fontSize: 'var(--font-size-sm)',
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        marginBottom: 'var(--spacing-xl)',
        textAlign: 'left',
        whiteSpace: 'normal',
    },
    /* Search & Categories */
    searchContainer: { marginBottom: 'var(--spacing-xl)' },
    searchInputWrapper: { position: 'relative', maxWidth: '600px', marginInline: 'auto' },
    searchIcon: { position: 'absolute', left: 'var(--spacing-md)', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' },
    searchInput: {
        width: '100%',
        paddingTop: 'var(--spacing-md)',
        paddingBottom: 'var(--spacing-md)',
        paddingLeft: 'calc(var(--spacing-md) * 2 + 20px)',
        paddingRight: 'var(--spacing-md)',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: { default: 'var(--border)', ':focus': 'var(--primary)' },
        borderRadius: 'var(--radius-full)',
        backgroundColor: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        outline: 'none',
    },
    searchResults: { textAlign: 'center', color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)', marginTop: 'var(--spacing-sm)' },

    /* Tabs/Category List */
    categoriesContainer: { display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' },
    categoryItem: { borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', backgroundColor: 'var(--bg-primary)' },
    categoryHeader: { backgroundColor: { default: 'var(--bg-card)', ':hover': 'var(--bg-tertiary)' }, padding: 'var(--spacing-md) var(--spacing-lg)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    categoryName: { display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', fontWeight: 600, fontSize: 'var(--font-size-lg)' },
    itemCount: { color: 'var(--text-secondary)', fontWeight: 400, fontSize: 'var(--font-size-sm)' },
    categoryContent: { padding: 'var(--spacing-lg)' },
    soundEffectsList: { display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' },

    /* Gallery Items */
    speechGalleryList: { display: 'grid', gridTemplateColumns: { default: 'repeat(auto-fit, minmax(400px, 1fr))', [MEDIA_768]: '1fr' }, gap: 'var(--spacing-md)', marginTop: 'var(--spacing-xl)' },
    speechGalleryItem: {
        backgroundColor: 'var(--bg-card)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: { default: 'var(--border)', ':hover': 'var(--primary)' },
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--spacing-md)',
        display: 'flex',
        alignItems: 'center',
        minHeight: '60px',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        backgroundImage: 'linear-gradient(135deg, var(--bg-card) 0%, rgba(59, 130, 246, 0.02) 100%)',
        flexDirection: { [MEDIA_768]: 'column' },
    },
    soundEffectGalleryItem: {
        backgroundColor: 'var(--bg-card)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--spacing-md)',
        display: 'flex',
        alignItems: 'center',
        minHeight: '60px',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        backgroundImage: 'linear-gradient(135deg, var(--bg-card) 0%, rgba(59, 130, 246, 0.02) 100%)',
        flexDirection: { [MEDIA_768]: 'column' },
    },
    soundEffectItemHovered: {
        borderColor: 'var(--primary)',
        boxShadow: 'var(--shadow-lg)',
        transform: 'translateY(-2px)',
    },

    /* Components Metadata */
    voiceInfoContainer: { flex: 1, display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' },
    soundEffectInfoContainer: { flex: 1, display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' },
    voiceName: { fontSize: 'var(--font-size-md)', fontWeight: 600, margin: 0 },
    soundEffectPrompt: { fontWeight: 600, fontSize: 'var(--font-size-md)', margin: 0 },

    audioPlayerContainer: { flexShrink: 0, width: { default: '45%', [MEDIA_768]: '100%' } },
    audioPlayer: {
        width: '100%',
        height: '32px',
        '::-webkit-media-controls-panel': {
            backgroundColor: '#9ca5afff',
            color: 'white',
        }
    },

    /* Click Indicators */
    clickIndicatorSpeech: {
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--primary)',
        paddingBlock: 'var(--spacing-xs)',
        paddingInline: 'var(--spacing-sm)',
        borderRadius: 'var(--radius-full)',
        fontSize: '0.75rem',
        color: 'var(--primary)',
        opacity: 0,
        transform: 'scale(0.8)',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        backdropFilter: 'blur(10px)',
    },
    clickIndicatorVisible: { opacity: 1, transform: 'scale(1)' },
    indicatorVisible: { opacity: 1, transform: 'scale(1)' },

    sparkle: { color: 'var(--accent)' },
});